import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import logo from '../assets/images/matcha-logo.svg'
import './TutorialPage.css'

const TutorialPage = () => {
  // Combined Demo: Failed match first, then successful match
  const [demoCards, setDemoCards] = useState([
    { id: 1, value: '🌟', isFlipped: false, isMatched: false, isUnmatched: false },
    { id: 2, value: '🎯', isFlipped: false, isMatched: false, isUnmatched: false },
    { id: 3, value: '🎨', isFlipped: false, isMatched: false, isUnmatched: false },
    { id: 4, value: '🚀', isFlipped: false, isMatched: false, isUnmatched: false },
    { id: 5, value: '🎨', isFlipped: false, isMatched: false, isUnmatched: false },
    { id: 6, value: '🔥', isFlipped: false, isMatched: false, isUnmatched: false }
  ])
  const [demoStep, setDemoStep] = useState(0)

  // Combined Animation: Failed match first, then successful match
  useEffect(() => {
    const steps = [
      // Initial preview - show all cards
      () => {
        setDemoCards(cards => cards.map(c => ({ ...c, isFlipped: true, isUnmatched: false })))
      },
      // Hide all cards after preview
      () => {
        setDemoCards(cards => cards.map(c => ({ ...c, isFlipped: false, isUnmatched: false })))
      },
      // FAILED MATCH: Click first card (🌟 at position 0)
      () => {
        setDemoCards(cards => cards.map((c, i) =>
          i === 0 ? { ...c, isFlipped: true, isUnmatched: false } : c
        ))
      },
      // FAILED MATCH: Click second card (🎯 at position 1) - they don't match!
      () => {
        setDemoCards(cards => cards.map((c, i) =>
          i === 1 ? { ...c, isFlipped: true, isUnmatched: false } : c
        ))
      },
      // FAILED MATCH: Show red (unmatched state)
      () => {
        setDemoCards(cards => cards.map((c, i) =>
          i === 0 || i === 1 ? { ...c, isUnmatched: true } : c
        ))
      },
      // FAILED MATCH: Flip both cards back (no match)
      () => {
        setDemoCards(cards => cards.map((c, i) =>
          i === 0 || i === 1 ? { ...c, isFlipped: false, isUnmatched: false } : c
        ))
      },
      // SUCCESSFUL MATCH: Click first card (🎨 at position 2)
      () => {
        setDemoCards(cards => cards.map((c, i) =>
          i === 2 ? { ...c, isFlipped: true, isUnmatched: false } : c
        ))
      },
      // SUCCESSFUL MATCH: Click second card (🎨 at position 4) - they match!
      () => {
        setDemoCards(cards => cards.map((c, i) =>
          i === 4 ? { ...c, isFlipped: true, isUnmatched: false } : c
        ))
      },
      // SUCCESSFUL MATCH: Mark both as matched (yellow)
      () => {
        setDemoCards(cards => cards.map(c =>
          c.value === '🎨' ? { ...c, isMatched: true } : c
        ))
      },
      // Wait a moment before reset
      () => {},
      // Reset for next loop
      () => {
        setDemoCards([
          { id: 1, value: '🌟', isFlipped: false, isMatched: false, isUnmatched: false },
          { id: 2, value: '🎯', isFlipped: false, isMatched: false, isUnmatched: false },
          { id: 3, value: '🎨', isFlipped: false, isMatched: false, isUnmatched: false },
          { id: 4, value: '🚀', isFlipped: false, isMatched: false, isUnmatched: false },
          { id: 5, value: '🎨', isFlipped: false, isMatched: false, isUnmatched: false },
          { id: 6, value: '🔥', isFlipped: false, isMatched: false, isUnmatched: false }
        ])
        setDemoStep(0)
      }
    ]

    const interval = setInterval(() => {
      if (demoStep < steps.length) {
        steps[demoStep]()
        setDemoStep(prev => prev + 1)
      }
    }, 1000) // 30% faster: 1000ms instead of 1500ms

    return () => clearInterval(interval)
  }, [demoStep])

  return (
    <div className="tutorial-page">
      <div className="tutorial-container">
        <Link to="/" className="back-button">← Back to Game</Link>

        <h1>How to Play Matcha</h1>
        <p className="tutorial-intro">
          Match all pairs of cards to win! Watch these animated demos to learn the basics.
        </p>

        {/* Combined Demo: Failed Match → Successful Match */}
        <div className="demo-section">
          <h2>🎮 How Matching Works</h2>
          <p className="demo-description">
            Watch this demo to see both a failed match and a successful match in action!
          </p>
          <div className="demo-grid demo-grid-2x3">
            {demoCards.map((card, index) => (
              <div
                key={card.id}
                className={`tutorial-card ${card.isFlipped ? 'flipped' : ''} ${card.isMatched ? 'matched' : ''} ${card.isUnmatched ? 'unmatched' : ''}`}
              >
                <div className="tutorial-card-inner">
                  <div className="tutorial-card-front">
                    <img src={logo} alt="Matcha Logo" className="tutorial-card-logo" />
                  </div>
                  <div className="tutorial-card-back">{card.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tips Section */}
        <div className="tips-section">
          <h2>💡 Pro Tips</h2>
          <ul className="tips-list">
            <li>
              <strong>Pay attention during the preview:</strong> All cards show briefly at the start. Memorize their positions!
            </li>
            <li>
              <strong>Work methodically:</strong> Try to remember which cards you've already flipped.
            </li>
            <li>
              <strong>Story Mode:</strong> Complete levels to unlock new challenges with move and time limits!
            </li>
          </ul>
        </div>

        <Link to="/" className="start-playing-btn">
          Start Playing! 🎮
        </Link>
      </div>
    </div>
  )
}

export default TutorialPage
